// web-staking-estimator-frontend/src/components/forms/ServiceConfigurationForm.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { InfoButton } from "../common/InfoButton";
import { useServiceOptions } from "../../hooks/useServiceOptions";
import { validateIrrigationHP } from "../../utils/validation";

export default function ServiceConfigurationForm({
  category,
  value,
  onChange,
  selectedPoint,
  xfmrOHUGHint,
  nearestNone
}) {
  const safeCategory = (category || "").toLowerCase();
  const [cannotEstimateMsg, setCannotEstimateMsg] = useState(null);
  const [noNearestMsg, setNoNearestMsg] = useState(null);
  const [serviceDefaults, setServiceDefaults] = useState({});
  const [irrigationConfig, setIrrigationConfig] = useState({});
  const [openTip, setOpenTip] = useState(null);
  const bannerRef = useRef(null);

  const v = value || {};

  // Pretty UI label from previous screen
  const enforcedSvcTypePretty =
    safeCategory === "irrigation" ? "Irrigation" :
    safeCategory === "commercial" ? "Commercial" : "Residential";

  // Load config messages, service defaults, and irrigation HP config
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/config", { headers: { Accept: "application/json" } });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (!cancelled) {
          setCannotEstimateMsg(j?.config?.messages?.cannotEstimate || null);
          setNoNearestMsg(j?.config?.messages?.noNearestInfrastructure || null);
          setServiceDefaults(j?.config?.estimation?.serviceDefaults || {});
          setIrrigationConfig(j?.config?.irrigation || {});
        }
      } catch {
        if (!cancelled) {
          setCannotEstimateMsg(null);
          setServiceDefaults({});
          setIrrigationConfig({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Use the service options hook
  const {
    services,
    phases,
    volts,
    amps,
    ohugOptions,
    xfmrOHUG,
    optLoading,
    matchedService,
    normOHUG,
  } = useServiceOptions({
    category,
    value,
    selectedPoint,
    xfmrOHUGHint,
    cannotEstimateMsg,
  });

  // Resolve service type to match data
  const types = useMemo(() => {
    const fold = (s) => (s ?? "").toString().trim().toLowerCase();
    return services
      .map((s) => s.svc_type)
      .filter((x, i, arr) => (x ?? "").toString().trim() !== "" && arr.indexOf(x) === i)
      .sort((a, b) => String(a).localeCompare(String(b)));
  }, [services]);

  const resolvedSvcType = useMemo(() => {
    const fold = (s) => (s ?? "").toString().trim().toLowerCase();
    const candidates = [enforcedSvcTypePretty, safeCategory, (enforcedSvcTypePretty || "").toLowerCase()];
    for (const cand of candidates) {
      const hit = types.find((t) => fold(t) === fold(cand));
      if (hit) return hit;
    }
    return types[0] || enforcedSvcTypePretty;
  }, [types, enforcedSvcTypePretty, safeCategory]);

  // Clear blocked helper
  function clearBlockedPatch(patch) {
    return { ...v, ...patch, blocked: false, blocked_reason: null };
  }

  // Keep v.svc_type aligned with resolvedSvcType
  useEffect(() => {
    if (v.svc_type !== resolvedSvcType) {
      onChange?.(clearBlockedPatch({
        svc_type: resolvedSvcType,
        svc_phase: null,
        svc_volts: null,
        svc_amps: null,
        svc_ohug: null,
        matched_service_id: null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSvcType]);

  // Clear svc_ohug when hint changes
  useEffect(() => {
    // Only call onChange if values actually need to change
    if (v.svc_ohug != null || v.matched_service_id != null || v.blocked) {
      onChange?.({ ...v, svc_ohug: null, matched_service_id: null, blocked: false, blocked_reason: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xfmrOHUGHint]);

  // Ensure matched_service_id is present
  useEffect(() => {
    if (v.matched_service_id != null) return;
    onChange?.({ ...v, matched_service_id: matchedService?.id ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedService]);

  // Toggle blocked based on matchedService.can_estimate
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log("[SCF:blocking check]", {
        hasMatchedService: !!matchedService,
        matchedServiceId: matchedService?.id,
        canEstimate: matchedService?.can_estimate,
        currentBlocked: v.blocked,
        ohugOptions: ohugOptions.length,
        svcOhug: v.svc_ohug,
        optLoading,
        cannotEstimateMsg
      });
    }
    
    if (!matchedService) {
      // GATE: Don't block while options are still loading (prevents premature error during map operations)
      if (optLoading) {
        if (process.env.NODE_ENV === 'development') {
          console.log("[SCF:blocking] Options still loading, deferring block check");
        }
        return;
      }
      
      // Check if we should block due to invalid/unavailable configuration
      const isIrrigation = safeCategory === 'irrigation';
      const hasBasicInputs = v.svc_phase && v.svc_volts && 
        (isIrrigation ? (v.svc_hp != null && v.svc_hp > 0) : v.svc_amps);
      
      // Scenario 1: No OH/UG options available (user can't proceed)
      const noOptionsAvailable = hasBasicInputs && ohugOptions.length === 0;
      
      // Scenario 2: User selected everything but no match found
      const fullInputsNoMatch = hasBasicInputs && v.svc_ohug;
      
      if ((noOptionsAvailable || fullInputsNoMatch) && !v.blocked && cannotEstimateMsg) {
        if (process.env.NODE_ENV === 'development') {
          console.log("[SCF:blocking] Invalid configuration detected:", { noOptionsAvailable, fullInputsNoMatch });
        }
        onChange?.({ ...v, blocked: true, blocked_reason: cannotEstimateMsg });
      }
      return;
    }
    
    const isBlocked = (String(matchedService?.can_estimate || "").toLowerCase() === "no") && cannotEstimateMsg;
    
    if (process.env.NODE_ENV === 'development') {
      console.log("[SCF:blocking] Matched service check:", { isBlocked, cannotEstimateMsg });
    }
    
    if (v.blocked !== isBlocked || (isBlocked && v.blocked_reason !== cannotEstimateMsg)) {
      onChange?.({ ...v, blocked: isBlocked, blocked_reason: isBlocked ? cannotEstimateMsg : null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedService, cannotEstimateMsg, ohugOptions.length, v.svc_phase, v.svc_volts, v.svc_amps, v.svc_hp, v.svc_ohug, safeCategory]);

  // Service type defaults from config
  useEffect(() => {
    const cat = (category || "").toString().trim().toLowerCase();
    const defaults = serviceDefaults[cat] || {};
    const patch = {};
    
    // Apply phase default
    const defaultPhase = defaults.phase;
    if (defaultPhase != null && (v.svc_phase == null || !phases.includes(Number(v.svc_phase))) && phases.includes(defaultPhase)) {
      patch.svc_phase = defaultPhase;
      patch.svc_volts = null;
      patch.svc_amps = null;
      patch.svc_ohug = null;
      patch.matched_service_id = null;
    }
    
    // Apply volts default
    const defaultVolts = defaults.volts;
    if (defaultVolts && (!v.svc_volts || !volts.includes(v.svc_volts)) && volts.includes(defaultVolts)) {
      patch.svc_volts = defaultVolts;
      patch.svc_amps = null;
      patch.matched_service_id = null;
    }
    
    // Apply amps default
    const defaultAmps = defaults.amps;
    if (defaultAmps != null && (v.svc_amps == null || !amps.includes(Number(v.svc_amps))) && amps.includes(defaultAmps)) {
      patch.svc_amps = defaultAmps;
      patch.matched_service_id = null;
    }
    
    if (Object.keys(patch).length) onChange?.(clearBlockedPatch(patch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, phases.length, volts.length, amps.length, serviceDefaults]);

  // HP validation for irrigation - trigger blocked state
  useEffect(() => {
    if (safeCategory === 'irrigation' && v.svc_hp != null && v.svc_phase != null) {
      const validation = validateIrrigationHP(v.svc_hp, v.svc_phase, irrigationConfig?.hp);
      if (!validation.valid && !v.blocked) {
        onChange?.({ ...v, blocked: true, blocked_reason: validation.error });
      } else if (validation.valid && v.blocked && v.blocked_reason && 
                 (v.blocked_reason.includes('HP') || v.blocked_reason.includes('Horsepower'))) {
        // Clear HP-related blocks when validation passes
        onChange?.({ ...v, blocked: false, blocked_reason: null });
      }
    }
  }, [v.svc_hp, v.svc_phase, safeCategory, irrigationConfig, v.blocked, v.blocked_reason, onChange]);

  // Focus/scroll to banner when it appears
  useEffect(() => {
    if ((v.blocked || nearestNone) && bannerRef.current) {
      try { bannerRef.current.focus(); } catch {}
      try { bannerRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.blocked, nearestNone]);

  const bannerText =
    (nearestNone ? noNearestMsg : null) ||
    v.blocked_reason ||
    cannotEstimateMsg;

  const havePin = Number.isFinite(selectedPoint?.x) && Number.isFinite(selectedPoint?.y) && Number.isFinite(selectedPoint?.wkid);
  const haveHint = !!normOHUG(xfmrOHUGHint);
  const ohugDisabled = optLoading || ohugOptions.length === 0 || !havePin || !haveHint;

  function onSvcOhugChange(e) {
    const next = normOHUG(e.target.value || null) || null;
    onChange?.({ ...v, svc_ohug: next, matched_service_id: null, blocked: false, blocked_reason: null });
    if (process.env.NODE_ENV === 'development') {
      console.log("[SCF:svc_ohug change]", { chosen: next });
    }
  }

  return (
    <div>
      {/* Error banner */}
      {(v.blocked || nearestNone) && (
        <div
          ref={bannerRef}
          className="banner banner-err"
          tabIndex={-1}
          role="alert"
          aria-live="assertive"
          style={{ marginBottom: 12 }}
        >
          {bannerText}
        </div>
      )}

      {/* Service Type (read-only) */}
      <div className="formRow">
        <label className="label">Service Type</label>
        <input className="input" value={enforcedSvcTypePretty} disabled />
      </div>

      {/* Phase */}
      <div className="formRow">
        <label className="label" htmlFor="svc_phase">
          Phase
          <InfoButton id="phase" label="Phase" isOpen={openTip==="phase"} onToggle={(v)=>setOpenTip(v?"phase":null)}>
            1 refers to single phase (generally residential/smaller). 3 refers to three phase (generally commercial/industrial).
          </InfoButton>
        </label>
        <select
          id="svcPhase"
          className="input"
          value={v.svc_phase ?? ""}
          onChange={(e) => onChange?.(clearBlockedPatch({
            svc_phase: Number(e.target.value) || null,
            svc_volts: null, svc_amps: null,
            svc_ohug: null, matched_service_id: null
          }))}
          disabled={!v.svc_type || phases.length === 0}
        >
          <option value="">{!v.svc_type ? "Select a type first" : phases.length ? "— Select —" : "No phases available"}</option>
          {phases.map((p) => (
            <option key={String(p)} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* Volts */}
      <div className="formRow">
        <label className="label" htmlFor="svc_volts">
          Volts
          <InfoButton id="volts" label="Volts" isOpen={openTip==="volts"} onToggle={(v)=>setOpenTip(v?"volts":null)}>
            120/240 is generally for residential homes. 120/208 or 277/480 is generally for commercial/industrial services.
          </InfoButton>
        </label>
        <select
          id="svcVolts"
          className="input"
          value={v.svc_volts || ""}
          onChange={(e) => onChange?.(clearBlockedPatch({
            svc_volts: e.target.value || null,
            svc_amps: null,
            svc_ohug: null, matched_service_id: null
          }))}
          disabled={!v.svc_phase || volts.length === 0}
        >
          <option value="">{!v.svc_phase ? "Select phase first" : volts.length ? "— Select —" : "No volt options"}</option>
          {volts.map((vv) => (
            <option key={String(vv)} value={vv}>{vv}</option>
          ))}
        </select>
      </div>

      {/* HP (Irrigation) or Amps (Residential/Commercial) */}
      {safeCategory === 'irrigation' ? (
        <div className="formRow">
          <label className="label" htmlFor="svc_hp">
            Horsepower (HP)
            <InfoButton id="hp" label="Horsepower" isOpen={openTip==="hp"} onToggle={(v)=>setOpenTip(v?"hp":null)}>
              Enter the horsepower rating for your irrigation pump. 1-phase service supports up to 25 HP, 3-phase supports up to 100 HP.
            </InfoButton>
          </label>
          <input
            id="svcHp"
            type="number"
            className="input"
            value={v.svc_hp ?? ""}
            onChange={(e) => {
              const hp = e.target.value === "" ? null : Number(e.target.value);
              onChange?.(clearBlockedPatch({
                svc_hp: hp,
                svc_ohug: null,
                matched_service_id: null
              }));
            }}
            disabled={!v.svc_volts}
            placeholder={!v.svc_volts ? "Select volts first" : "Enter HP"}
            step="0.01"
            min="0"
          />
        </div>
      ) : (
        <div className="formRow">
          <label className="label" htmlFor="svc_amps">
            Amps
            <InfoButton id="amps" label="Amps" isOpen={openTip==="amps"} onToggle={(v)=>setOpenTip(v?"amps":null)}>
              This is the service size. Residential homes are generally 200A or 400A. Commercial/industrial can be 200A, 400A, or greater.
            </InfoButton>
          </label>
          <select
            id="svcAmps"
            className="input"
            value={v.svc_amps ?? ""}
            onChange={(e) => onChange?.(clearBlockedPatch({
              svc_amps: Number(e.target.value) || null,
              svc_ohug: null, matched_service_id: null
            }))}
            disabled={!v.svc_volts || amps.length === 0}
          >
            <option value="">{!v.svc_volts ? "Select volts first" : amps.length ? "— Select —" : "No amp options"}</option>
            {amps.map((a) => (
              <option key={String(a)} value={a}>{a}</option>
            ))}
          </select>
        </div>
      )}

      {/* OH/UG */}
      <div className="formRow">
        <label className="label" htmlFor="svc_ohug">
          Overhead or underground service?
          <InfoButton id="ohug" label="Overhead or Underground" isOpen={openTip==="ohug"} onToggle={(v)=>setOpenTip(v?"ohug":null)}>
            Where available, overhead service is made by aerial cables to your service location, and underground service is made by buried cables in conduit to your service location.
          </InfoButton>
        </label>
        <select
          id="svcOhug"
          className="input"
          value={v.svc_ohug ?? ""}
          onChange={onSvcOhugChange}
          disabled={ohugDisabled}
          title={
            !havePin ? "Drop a pin on the map first"
            : !haveHint ? "Nearest infrastructure not detected yet"
            : (optLoading ? "Loading…" : undefined)
          }
        >
          <option value="">
            {optLoading ? "Loading…" : ohugOptions.length ? "— Select —" : "No options"}
          </option>
          {ohugOptions.map((opt) => {
            const label = String(opt).toUpperCase() === "OH" ? "Overhead" : (String(opt).toUpperCase() === "UG" ? "Underground" : String(opt));
            return <option key={opt} value={opt}>{label}</option>;
          })}
        </select>
      </div>
    </div>
  );
}
